<template>
  <template>
    <dx-data-grid
      ref="dataGridRef"
      :allow-column-reordering="true"
      :column-chooser="{ enabled: true, mode: 'select' }"
      :column-min-width="50"
      :columns="columns"
      :data-source="dataSource"
      :filter-row="{ visible: true }"
      :header-filter="{ visible: true }"
      :height="50"
      :hover-state-enabled="true"
      :repaint-changes-only="true"
      :row-alternation-enabled="true"
      :show-borders="true"
      :show-column-lines="true"
      :width="'100%'"
      :word-wrap-enabled="true"
      style="flex-grow: 1"
      value-expr="kod_sp_calc"
      @toolbar-preparing="onToolbarPreparing($event)"
      @cell-prepared="onCellPrepared($event)"
      @cell-dbl-click="openDetail($event)"
      @content-ready="onContentReady($event)"
    >
      <dx-paging :enabled="true" :page-size="40" />
      <dx-load-panel :enabled="true" />
      <dx-scrolling mode="virtual" />
      <dx-sorting mode="single" />
      <dx-export :allow-export-selected-data="true" :enabled="true" :formats="['pdf', 'excel']" />

      <template #filterTemplatePeriod>
        <mped-calculation-period-component
          :default-value="defaultValue"
          :default-period="'current'"
          @value-changed="onPeriodChanged($event)"
        />
      </template>
    </dx-data-grid>

    <div class="flex justify-content-between items-center">
      <div class="ai-tab-bar-container d-flex">
        <div v-for="item in totalReportPages" :key="item.id">
          <ai-tab-bar-item
            :id="item.id"
            :key="item.id"
            :name="item.name"
            :view="{ ...item, buttonCloseHide: true }"
            :use-route="false"
            :focused="item.id === selectedPageId"
            @item-click="onTabTitleClick" />
        </div>
      </div>
      <footer-grid :show-selected-counter="false" :totalCount="totalCount" class="px-2"></footer-grid>
    </div>
  </template>
</template>

<script>
export default {
  name: 'mped-calculation-analysis-report-view',
};
</script>

<script setup>
import { onBeforeMount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  DxDataGrid,
  DxScrolling,
  DxLoadPanel,
  DxSorting,
  DxExport,
  DxPaging,
} from 'devextreme-vue/data-grid';

import AiTabBarItem from '@/layouts/main-layout/components/tab-bar/tab-bar-item.vue';
import FooterGrid from '@/components/grid-card-component/footer-grid.vue';
import MpedCalculationPeriodComponent
  from '@/views/mped/views/calculation/component/mped-calculation-period-component.vue';
import { getAiToolbar } from '@/components/utils/ai-toolbar-generator';
import { getAiDataSource } from '@/components/utils/ai-data-source-generator';
import { getCalculationAnalysisReport, getCalculationAnalysisDetail } from '@/views/mped/services/mped-calculation-api-service';
import { columnsCalculationReport, totalReportPages } from '@/views/mped/services/mped-calculation-service';
import { baseUrl, appName } from '@/views/mped/services/mped-service';
import { getLocalUserdata, initState, setLocalUserdata } from '@/service/local-userdata-service';


const totalCount = ref(0);
const dataGridRef = ref();
const selectedPageId = ref(1);
const route = useRoute();
const router = useRouter();

let periods;
let defaultValue = null;
let columns;
let globalData;
let focusedCellTarget = null;

const dataSource = getAiDataSource(
  {
    dataFn: async () => {
      if (!globalData)
        return [];
      const selectedPage = totalReportPages.find(page => page.id === selectedPageId.value);
      if (selectedPage.id === 7) return globalData.reference;
      else return globalData.filials.filter(row => row.excel_list === selectedPage.title);
    },
    key: 'ord',
    onLoadedFn: (data) => {
      totalCount.value = data.totalCount;
      focusCell();
    },
  },
);

onBeforeMount(() => {
  const state = getLocalUserdata(router);
  defaultValue = state?.filters?.periods || null;
  columns = columnsCalculationReport();
});

onMounted(async () => {
  const state = getLocalUserdata(router);
  initState(router, dataGridRef);
  if (state) {
    selectedPageId.value = state.filters.selectedPageId;
    periods = state.filters?.periods || null;
    globalData = state.dataSource;
    dataSource.reload();
  }
});

const reloadData = async () => {
  setState();
  globalData = await getCalculationAnalysisReport({ periods });
  dataSource.reload();
};

const onPeriodChanged = (e) => {
  periods = e;
};

const onToolbarPreparing = (e) => {
  getAiToolbar(e, {
    customs: [{
      location: 'before',
      template: 'filterTemplatePeriod',
    }],
    refresh: {
      event: reloadData,
    },
  });
};

const onTabTitleClick = (view) => {
  if (selectedPageId.value !== view.id) {
    const prevPage = totalReportPages.find(page => page.id === selectedPageId.value);
    if (prevPage.viewId !== view.viewId)
      changeVisibleColumns(view);
    selectedPageId.value = view.id;
    setState();
    dataSource.reload();
  }
};

const changeVisibleColumns = (view) => {
  columns.forEach(x => {
    if (x.viewId)
      x.showInColumnChooser = x.visible = x.viewId.includes(view.viewId);
  });
  dataGridRef.value.instance.option('columns', columns);
};

const onCellPrepared = (e) => {
  if (e.rowType === 'data') {
    const { name } = e.row.data;
    if (name.includes('Сумма по'))
      e.cellElement.classList.add('calculation-report-text-summ');
    else if (name.includes('Итого производство'))
      e.cellElement.classList.add('calculation-report-text-production');
    else if (name.includes(' (Итого)'))
      e.cellElement.classList.add('calculation-report-text-total');
  }
};

const openDetail = (e) => {
  if (e.rowType === 'data') {
    const { excel_list: sheet, name: row } = e.data;
    const col = e.column.dataField;
    focusCell(e);
    getCalculationAnalysisDetail({ periods, sheet, row, col }).then(data => {
      const guid = data.processGuid;
      const { app, dep } = route.params;
      if (guid)
        window.open(`/${baseUrl}/${appName}/calculation-analysis-detail/&/${app}/${dep}/${guid}/${getCaptionDetail(e)}`);
    });
  }
};

const focusCell = (e) => {
  if (focusedCellTarget)
    focusedCellTarget.classList.remove('calculation-report-text-focused')

  if (e) {
    focusedCellTarget = e.event.target;
    focusedCellTarget.classList.add('calculation-report-text-focused')
  }
}
const getCaptionDetail = (e) => {
  const selectedPage = totalReportPages.find(page => page.id === selectedPageId.value);
  const captionPath = [periods, selectedPage.title,  e.row.data['name'], e.column.caption];
  return captionPath.join('; ').replaceAll('/', '-');
}

const onContentReady = e => {
  setState();
}

const setState = () => {
  setLocalUserdata(router, dataGridRef, { periods, selectedPageId: selectedPageId.value }, globalData);
}

</script>

<style>
.calculation-report-text-production {
  color: #934848;
  font-weight: bold;
}

.calculation-report-text-summ {
  color: #4f7ecb;
  font-weight: bold;
}

.calculation-report-text-total {
  font-weight: bold;
}

.calculation-report-text-focused {
  font-weight: bold !important;
  color: green;
}
</style>

<style scoped>

</style>
